import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { Option } from "../../../models/option.model";
import { RiskAppetiteBusinessLine } from "../../../models/risk-appetite-business-line.model";
import { BusinessLineAppetiteRow } from "../../../models/business-line-appetite-row.model";
import { BusinessLineAppetiteEnum } from "../../../models/business-line-appetite.enum";
import { NgClass } from "@angular/common";
import { BusinessLineAppetiteOption } from "../../../models/business-line-appetite-option.model";

/**
 * PURPOSE: responsible for displaying the business line appetite table and associated selection counts
 */
@Component({
  selector: "app-business-line-appetite",
  standalone: true,
  imports: [NgClass],
  templateUrl: "./business-line-appetite.component.html",
  styleUrl: "./business-line-appetite.component.scss"
})
export class BusinessLineAppetiteComponent implements OnChanges {
  @Input() isNewMarketplaceCreationRequest: boolean = false;
  @Input() businessLines: Option[] = [];
  @Input() riskAppetiteBusinessLines: RiskAppetiteBusinessLine[] = [];
  @Output() businessLineAppetiteChanged: EventEmitter<BusinessLineAppetiteRow> =
    new EventEmitter<BusinessLineAppetiteRow>();
  @Output() allBusinessLinesAppetitesChanged: EventEmitter<Map<string, BusinessLineAppetiteEnum>> =
    new EventEmitter<Map<string, BusinessLineAppetiteEnum>>();

  private businessLineAppetitesMap: Map<string, BusinessLineAppetiteEnum> = new Map<
    string,
    BusinessLineAppetiteEnum
  >();
  protected businessLineAppetiteEnum: typeof BusinessLineAppetiteEnum = BusinessLineAppetiteEnum;
  protected businessLineAppetiteOptions: BusinessLineAppetiteOption[] = [
    { appetite: BusinessLineAppetiteEnum.OPTIONAL, displayName: "Optional" },
    { appetite: BusinessLineAppetiteEnum.REQUIRED, displayName: "Required" },
    { appetite: BusinessLineAppetiteEnum.EXCLUDED, displayName: "Excluded" }
  ];
  /**
   * Updates the business line appetite map when input data changes and emits the changes
   */
  ngOnChanges(): void {
    this.businessLines
      .filter((businessLine: Option): boolean => businessLine.field !== null)
      .forEach((businessLine: Option): void => {
        if (!this.businessLineAppetitesMap.has(businessLine.field!)) {
          this.updateBusinessLineAppetiteMap(businessLine.field!);
        }
      });

    this.emitAllBusinessLineAppetites();
  }

  /**
   * Updates the business line appetite map
   */
  private updateBusinessLineAppetiteMap(businessLineCode: string): void {
    let appetite: BusinessLineAppetiteEnum;

    const matchingRiskAppetiteBusinessLine: RiskAppetiteBusinessLine | undefined =
      this.riskAppetiteBusinessLines.find(
        (riskAppetiteBusinessLine: RiskAppetiteBusinessLine): boolean =>
          riskAppetiteBusinessLine.businessLineCode === businessLineCode
      );

    if (matchingRiskAppetiteBusinessLine) {
      appetite = matchingRiskAppetiteBusinessLine.excluded
        ? BusinessLineAppetiteEnum.EXCLUDED
        : BusinessLineAppetiteEnum.REQUIRED;
    } else {
      if (this.isNewMarketplaceCreationRequest) {
        appetite = BusinessLineAppetiteEnum.EXCLUDED;
      } else {
        appetite = BusinessLineAppetiteEnum.OPTIONAL;
      }
    }

    this.businessLineAppetitesMap.set(businessLineCode, appetite);
  }

  /**
   * Emit the complete business line appetite map
   */
  private emitAllBusinessLineAppetites(): void {
    this.allBusinessLinesAppetitesChanged.emit(new Map(this.businessLineAppetitesMap));
  }

  /**
   * Get the business line appetite data to be displayed in the table
   */
  protected get tableData(): BusinessLineAppetiteRow[] {
    return this.businessLines
      .filter((businessLine: Option): boolean => businessLine.field !== null)
      .map(
        (businessLine: Option): BusinessLineAppetiteRow => ({
          businessLineCode: businessLine.field!,
          businessLine: businessLine.displayName,
          appetite: this.getBusinessLineAppetite(businessLine.field!)
        })
      );
  }

  /**
   * Get the current business line appetite
   */
  private getBusinessLineAppetite(businessLineCode: string): BusinessLineAppetiteEnum {
    return this.businessLineAppetitesMap.get(businessLineCode)
      ? this.businessLineAppetitesMap.get(businessLineCode)!
      : BusinessLineAppetiteEnum.EXCLUDED;
  }

  /**
   * Get the applicable class for each business line appetite button
   */
  protected getAppetiteButtonClasses(
    newlySelectAppetite: BusinessLineAppetiteEnum,
    previouslySelectedAppetite?: BusinessLineAppetiteEnum | null
  ): string {
    const appetiteIsSelected: boolean =
      previouslySelectedAppetite != null && newlySelectAppetite === previouslySelectedAppetite;

    switch (newlySelectAppetite) {
      case BusinessLineAppetiteEnum.OPTIONAL:
        return appetiteIsSelected
          ? "bg-yellow-100 text-yellow-800 border border-yellow-300 ring-2 ring-yellow-200"
          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-200";
      case BusinessLineAppetiteEnum.REQUIRED:
        return appetiteIsSelected
          ? "bg-green-100 text-green-800 border border-green-300 ring-2 ring-green-200"
          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200";
      case BusinessLineAppetiteEnum.EXCLUDED:
        return appetiteIsSelected
          ? "bg-red-100 text-red-800 border border-red-300 ring-2 ring-red-200"
          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-300";
    }
  }

  /**
   * Updates the business line appetite map on appetite change and emit the changes
   */
  protected onAppetiteChange(
    businessLineCode: string,
    businessLine: string,
    newBusinessLineAppetite: BusinessLineAppetiteEnum
  ): void {
    this.businessLineAppetitesMap.set(businessLineCode, newBusinessLineAppetite);
    this.businessLineAppetiteChanged.emit({
      businessLineCode,
      businessLine,
      appetite: newBusinessLineAppetite
    });
    this.emitAllBusinessLineAppetites();
  }

  /**
   * Get the tooltip text for appetite buttons based on the appetite type
   */
  protected getAppetiteTooltip(businessLine: string, appetite: BusinessLineAppetiteEnum): string {
    switch (appetite) {
      case BusinessLineAppetiteEnum.REQUIRED:
        return `Referral must contain ${businessLine}`;
      case BusinessLineAppetiteEnum.OPTIONAL:
        return `Referral can contain ${businessLine} but it's not mandatory`;
      case BusinessLineAppetiteEnum.EXCLUDED:
        return `Referral will be rejected if it contains ${businessLine}`;
      default:
        return `Set ${businessLine} appetite`;
    }
  }

  /**
   * Get all current business line appetites
   */
  getAllBusinessLinesAppetites(): Map<string, BusinessLineAppetiteEnum> {
    return new Map(this.businessLineAppetitesMap);
  }

  /**
   * Get the current count of a business line appetite
   */
  getAppetiteCount(businessLineAppetiteEnum: BusinessLineAppetiteEnum): number {
    return this.tableData.filter(
      (row: BusinessLineAppetiteRow): boolean => row.appetite === businessLineAppetiteEnum
    ).length;
  }
  setAllAppetites(appetiteOption: BusinessLineAppetiteOption): void {
    const appetite = appetiteOption.appetite;
    this.tableData.forEach((row) => {
      this.onAppetiteChange(row.businessLineCode, row.businessLine, appetite);
    });
  }
}
